import React, { useState } from 'react';

import { Button, Segment, Image, Container, Message } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import {
  getGoogleLocationProviderSettings,
  getGoogleProfileProviderSetting,
  getGoogleLocationProviderSetting,
} from 'components/modules/business-profile/services/provider-service';
import useProviderServiceSettings from 'components/modules/integrations/hooks/use-provider-service-settings';
import { multiCaseGet, objectToSnakeCase } from 'libs/format';

import { GMB_AUTH_ERROR_KEY } from './ProfilePanel.constants';
import {
  hasGoogleMyBusinessToken,
  getErrorHeaderAndMessage,
  getGoogleMyBusinessAccountId,
  getLocationSettingsChangePayload,
  getCurrentStep,
  getAssociatedLocations,
  hasGoogleProfileServiceSetting,
  getConnectionSteps,
  getAccountSelectPayload,
} from './ProfilePanel.utils';
import API from '../../../../../../libs/api';
import { isAuthorizedForSaleViews } from '../../../../../../libs/auth';
import ErrorHandler from '../../../../../../libs/errors';
import {
  selectBusinessId,
  selectBusinessType,
  selectUserRoleForBusiness,
} from '../../../../../../selectors/business';
import useAsyncEffect from '../../../../foundation/hooks/use-async-effect';
import useBranding from '../../../../foundation/hooks/use-branding';
import AccountList from '../AccountList';
import AccountLocationList from '../AccountLocationList';
import AccountSettings from '../AccountSettings';
import ConnectionSteps from '../ConnectionSteps';

import GMBLogo from '../../../../../../static/logos/GMB-logo.png';

import './ProfilePanel.scss';

export default function ProfilePanel() {
  const businessId = useSelector(selectBusinessId);
  const businessType = useSelector(selectBusinessType);
  const userRole = useSelector(selectUserRoleForBusiness);

  const { providerServiceSettings, refreshSettings, updateSetting } = useProviderServiceSettings();

  const { brandName } = useBranding();

  const [accounts, setAccounts] = useState(null);
  const [locations, setLocations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentStep = getCurrentStep(providerServiceSettings);

  async function refreshGbp() {
    try {
      setLoading(true);
      setError(null);

      if (hasGoogleMyBusinessToken(providerServiceSettings)) {
        let newAccounts = await API.getGoogleMyBusinessAccounts(businessType, businessId);

        if (hasGoogleProfileServiceSetting(providerServiceSettings)) {
          const serviceSetting = getGoogleProfileProviderSetting(providerServiceSettings);
          newAccounts = newAccounts.filter(
            (account) =>
              multiCaseGet(account, 'accountId') ===
              multiCaseGet(serviceSetting, 'serviceData.gmbAccountId'),
          );
        }

        let newLocations = null;
        if (!_isEmpty(newAccounts)) {
          newLocations = await Promise.all(
            newAccounts.map((account) =>
              API.getGoogleMyBusinessLocations(
                businessType,
                businessId,
                multiCaseGet(account, 'accountId'),
              ),
            ),
          );
          newLocations = newLocations.flat();
        }

        setAccounts(newAccounts);
        setLocations(newLocations);
      }
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onAccountSelect(data) {
    try {
      setLoading(true);
      const serviceSetting = getGoogleProfileProviderSetting(providerServiceSettings);
      const payload = getAccountSelectPayload(serviceSetting, data);
      await updateSetting(serviceSetting.id, payload);
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onLocationsAssociation(data) {
    try {
      setLoading(true);
      const payload = objectToSnakeCase(data);
      await API.addGoogleMyBusinessLocation(businessType, businessId, payload);
      await refreshSettings();
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onDisconnect() {
    try {
      setLoading(true);
      await API.disconnectGoogleMyBusiness(businessType, businessId);
      await refreshSettings();
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onLocationSettingsChange(associatedLocation, newSyncSettings) {
    try {
      setLoading(true);
      const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
      const serviceSetting = getGoogleLocationProviderSetting(locationSettings, associatedLocation);
      const { id: serviceId } = serviceSetting;
      const payload = getLocationSettingsChangePayload(serviceSetting, newSyncSettings);
      await updateSetting(serviceId, payload);
      setLoading(false);
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
      setLoading(false);
    }
  }

  async function getGoogleMyBusinessAuth() {
    try {
      setLoading(true);
      const response = await API.getGoogleMyBusinessAuth(businessType, businessId);
      if (response.data.url) {
        window.location.assign(response.data.url);
      }
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  useAsyncEffect(async () => {
    await refreshSettings();
    await refreshGbp();
  }, []);

  // Step renderers
  function renderAuthorize() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    let authError = null;
    if (urlSearchParams.has(GMB_AUTH_ERROR_KEY)) {
      const [authErrorHeader, authErrorMsg] = getErrorHeaderAndMessage(
        urlSearchParams.get(GMB_AUTH_ERROR_KEY),
        brandName,
      );
      authError = <Message negative header={authErrorHeader} content={authErrorMsg} />;
    }
    return (
      <>
        <Segment attached="bottom">
          {authError}
          <Message>
            <Message.Header>Connect {brandName} to Google</Message.Header>
            <p>
              By connecting your {brandName} website to Google, we can automatically update critical
              information about your business as you update it. To enable this functionality, you
              will need to authorize {brandName} to manage your business listing.
            </p>
          </Message>
          <Container textAlign="center">
            <Button size="big" primary onClick={getGoogleMyBusinessAuth} loading={loading}>
              Authorize {brandName}
            </Button>
          </Container>
        </Segment>
        {error && (
          <Segment attached="bottom">
            <Message negative>
              <Message.Header>Issue Configuring Google Business Profile</Message.Header>
              <p>{error.message || 'There was an error, please try again later.'}</p>
            </Message>
          </Segment>
        )}
      </>
    );
  }

  function renderSelectAccount() {
    if (loading) return <Segment attached="bottom" padded="very" loading />;
    return <AccountList accounts={accounts} onAccountSelect={onAccountSelect} />;
  }

  function renderSelectLocation() {
    if (loading) return <Segment attached="bottom" padded="very" loading />;
    return (
      <AccountLocationList
        gmbAccountId={getGoogleMyBusinessAccountId(providerServiceSettings)}
        gmbLocations={locations}
        onDisconnect={onDisconnect}
        onLocationsAssociation={onLocationsAssociation}
      />
    );
  }

  function renderAccountSettings() {
    const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
    const associatedLocations = getAssociatedLocations(locations, locationSettings);

    return (
      <AccountSettings
        loading={loading}
        associatedLocations={associatedLocations}
        locationSettings={locationSettings}
        onDisconnect={onDisconnect}
        onLocationSettingsChange={onLocationSettingsChange}
      />
    );
  }

  function renderIntro() {
    if (currentStep > 3) return null;
    return (
      <Segment attached="top">
        <Container textAlign="center">
          <Image src={GMBLogo} style={{ display: 'inline', margin: 0, height: '100px' }} />
        </Container>
        <Container>
          <p>
            <b>Unlock Your Online Visibility with Google Business Profile! </b>
            This powerful, free tool empowers businesses and organizations to take control of their
            Google presence, spanning Search and Maps. By verifying and refining your business
            details, you will not only make it easy for customers to discover you but also share the
            unique qualities that make your business special.
          </p>
        </Container>
      </Segment>
    );
  }

  if (isAuthorizedForSaleViews(userRole)) return null;

  return (
    <div className="google-business-profile-panel">
      {renderIntro()}
      <Segment attached>
        <ConnectionSteps steps={getConnectionSteps(currentStep, brandName)} />
      </Segment>
      {(() => {
        switch (currentStep) {
          case 1:
            return renderAuthorize();
          case 2:
            return renderSelectAccount();
          case 3:
            return renderSelectLocation();
          case 4:
            return renderAccountSettings();
          default:
            return null;
        }
      })()}
    </div>
  );
}
